import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * A render error anywhere below this point takes out only this subtree, not
 * the whole app. Without it React 19 unmounts the entire tree on a throw and
 * the user is left staring at a blank white page with no way back.
 *
 * Two levels are wired up in App.tsx: one around the shell (last resort) and
 * one inside the layout (so a broken page keeps the sidebar usable).
 */
type Props = {
  children: ReactNode;
  /** Changing this value clears the error — pass the route so navigation recovers. */
  resetKey?: string;
  variant?: 'page' | 'app';
};

type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Wire this to the server's /client-errors endpoint when you have one.
    // Never send the error message itself: it can contain resident names.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <ErrorFallback
        variant={this.props.variant ?? 'page'}
        onRetry={() => this.setState({ error: null })}
      />
    );
  }
}

function ErrorFallback({ variant, onRetry }: { variant: 'page' | 'app'; onRetry: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="error-boundary" role="alert">
      <AlertTriangle size={34} aria-hidden />
      <h2>{t('errors.crashTitle')}</h2>
      <p>{t('errors.crashBody')}</p>
      <div className="error-boundary__actions">
        <button type="button" className="button" onClick={onRetry}>
          <RotateCcw size={16} aria-hidden /> {t('errors.retry')}
        </button>
        {variant === 'app' && (
          <button type="button" className="button ghost" onClick={() => window.location.assign('/')}>
            {t('errors.backHome')}
          </button>
        )}
      </div>
    </div>
  );
}
